<template>
  <section class="p-2 mt-3">
    <b-row
      v-show="showContent"
      align-v="start"
    >
      <!-- Archivos -->
      <b-col
        cols="12"
        sm="12"
        md="7"
        xl="8"
      >

        <b-tabs
          v-model="currentTab"
          pills
          fill
          align="center"
          class="driver-tabs"
        >
          <!-- Cargar archivos -->
          <b-tab
            title="Carga de documentos"
            active
          >
            <b-card>
              <b-card-title class="text-center">
                Documentación obligatoria (faltante)
              </b-card-title>

              <b-alert
                class="driver-alert-file-name"
                variant="danger"
                show
              >
                <div class="alert-body text-center">
                  <p>
                    Al nombrar sus documentos, <strong>evite</strong> colocar nombres largos, acentos y caracteres especiales.
                  </p>
                </div>
              </b-alert>

              <section v-if="showContent && !showError">
                <div
                  v-for="tipo in tipoDocs"
                  :key="tipo.id"
                >
                  <input-file
                    v-if="!loadedFiles.find(doc => doc.tipo === tipo.id)"
                    class="mb-2"
                    :file-body="tipo"
                    :file-loaded="loadedFiles.find(doc => doc.tipo === tipo.id)"
                    :loader="fileLoader[tipo.subPath]"
                    @file-selected="files[tipo.subPath] = $event"
                    @file-removed="delete files[tipo.subPath]"
                  />
                </div>
              </section>
            </b-card>
          </b-tab>

          <!-- Archivos cargados -->
          <b-tab
            title="Archivos previamente cargados"
          >
            <b-card>
              <b-card-body class="driver-files-uploaded">
                <b-card-title class="text-center">
                  Archivos guardados
                </b-card-title>

                <!-- Mensaje de en caso de no tener los documentos -->
                <b-alert
                  variant="danger"
                  :show="loadedFiles.length === 0"
                >
                  <div class="alert-body text-center">
                    <small>No tienes archivos por el momento.</small>
                  </div>
                </b-alert>

                <section>
                  <document-list
                    :data="loadedFiles"
                    status-icon
                  />
                </section>
              </b-card-body>
            </b-card>
          </b-tab>
        </b-tabs>
      </b-col>

      <!-- Avisos y botones -->
      <b-col
        cols="12"
        sm="12"
        md="5"
        xl="4"
      >
        <b-card>
          <b-overlay
            :show="showLoader || showLoaderUploaded"
            opacity="0.50"
            rounded="sm"
          >
            <!-- Título de documentación obligatoria -->
            <b-card-title class="mb-1 card-container-title">
              <p class="m-0">
                Comencemos ...
              </p>

              <b-button
                v-b-tooltip.hover.bottom="'Abrir guía interactiva'"
                variant="warning"
                class="btn-icon driver-interactive-guide-button"
                @click="showDriverComponent()"
              >
                <feather-icon
                  size="15"
                  icon="InfoIcon"
                />
              </b-button>
            </b-card-title>

            <!-- Mensaje de envío de documentos para validación -->
            <b-alert
              v-height-fade.appear
              class="driver-alert-original-file"
              variant="danger"
              :show="showContent && !showError"
            >
              <div class="alert-body">
                <p class="mb-1">
                  <small>
                    Considere que los documentos a cargar deben <strong>ser escaneados del documento original, en formato PDF a color.</strong> No utilizar escáner de los teléfonos móviles, ya que no permiten la lectura de los códigos QR.
                  </small>
                </p>

                <p>
                  <small>
                    No se aceptan documentos escaneados en blanco y negro, escaneados de fotocopias, documentos con baja calidad que se perciban borrosos, fotografías de los documentos, reducciones miniaturas y/o documentos donde en la foto del aspirante no se distinga.
                  </small>
                </p>
              </div>
            </b-alert>

            <!-- Botones -->
            <div
              v-if="showContent && !showError"
              class="mt-2"
            >
              <div class="text-center mb-1">
                <span>
                  Da clic en <b>"Enviar documentos"</b> para cargar la información de manera exitosa.
                </span>
              </div>

              <!-- Enviar documentos -->
              <b-button
                class="driver-upload-files-button"
                variant="primary"
                type="submit"
                block
                @click="uploadFiles()"
              >
                <feather-icon
                  icon="SendIcon"
                  class="mr-50"
                />
                <span class="align-middle">Enviar documentos</span>
              </b-button>

              <!-- Leyenda de archivos cargados -->
              <b-alert
                v-height-fade.appear
                variant="primary"
                class="mt-1 badge-glow"
                :show="showUploadedAnyFile"
              >
                <div class="alert-body text-center">
                  <small>Una vez cargada tu documentación, <b>puedes cerrar esta pantalla</b>, o si te faltó algún archivo por cargar, puedes elegir la opción <b>"Cargar o continuar en otro momento"</b></small>
                </div>
              </b-alert>

              <!-- Separador -->
              <div class="divider my-2">
                <div class="divider-text">
                  ó
                </div>
              </div>

              <!-- Mensaje de en caso de no tener los documentos -->
              <b-alert
                v-height-fade.appear
                variant="warning"
                :show="showSendLater && showContent && !showError"
              >
                <div class="alert-body text-center">
                  <small>En caso de no tener los documentos a la mano, presione el botón <b>"Cargar o continuar en otro momento"</b>.</small>
                </div>
              </b-alert>

              <!-- Cargar en otro momento -->
              <b-button
                class="driver-upload-files-later-button"
                variant="warning"
                block
                @click="sendLater()"
              >
                <feather-icon
                  icon="ClockIcon"
                  class="mr-50"
                />
                <span class="align-middle">Cargar o continuar en otro momento</span>
              </b-button>
            </div>
          </b-overlay>
        </b-card>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import store from '@/store'
import documentList from '@/views/components/documentacion/documentList.vue'
import { assignAsPersonalDocument, getDocumentTypesForPlan, uploadedDocuments } from '@/views/components/documentacion/useDocumentacion'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { heightFade } from '@core/directives/animations'
import {
  formhttp,
  isEmptyObject, isObject,
  useRouter,
} from '@core/utils/utils'
import {
  computed,
  getCurrentInstance,
  onMounted,
  ref,
} from '@vue/composition-api'
import {
  BAlert,
  BButton,
  BCard,
  BCardBody,
  BCardTitle,
  BCol,
  BOverlay,
  BRow,
  BTab,
  BTabs,
  VBTooltip,
} from 'bootstrap-vue'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { useToast } from 'vue-toastification/composition'
import inputFile from './components/documentos/inputFile.vue'
import { getPersonId, sendLaterMail } from './useFormulario'

export default {
  components: {
    BButton,
    BCard,
    BCardBody,
    BCardTitle,
    BAlert,
    BOverlay,
    BTabs,
    BTab,
    BRow,
    BCol,

    documentList,
    inputFile,
  },
  directives: {
    'height-fade': heightFade,
    'b-tooltip': VBTooltip,
  },
  setup() {
    const toast = useToast()
    const showSendLater = ref(false)
    const showContent = ref(false)
    const showError = ref(false)
    const showLoader = ref(false)
    const showLoaderUploaded = ref(false)
    const showUploadedAnyFile = ref(false)
    const fileLoader = ref({})
    const messageError = ref('')
    const files = ref({})
    const loadedFiles = ref([])
    const { route, router } = useRouter() //* Use router propieties
    const { id, time } = route.value.params //* Get the token from params
    const perId = ref(null)
    const planId = ref(null)
    const currentRol = computed(() => store.state.cuenta.rolSelected) //* Return current role
    const uploadLaterModal = ref(false)
    const fileToPreview = ref(null)
    const tipoDocs = ref([])
    const currentTab = ref(0)
    const vm = getCurrentInstance().proxy
    const isAlreadySeenInteractiveGuide = window.document.cookie.split('; ').find(row => row.startsWith('isAlreadySeenInteractiveGuide='))?.split('=')[1]

    //* Interactive guide
    const driverGuide = driver({
      showProgress: true,
      progressText: '{{current}} de {{total}}',
      nextBtnText: 'Siguiente',
      prevBtnText: 'Anterior',
      doneBtnText: 'Terminar',
      showButtons: ['next', 'previous'],
      onDestroyed: () => {
        currentTab.value = 0
      },
      steps: [
        {
          element: '.driver-tabs li:nth-child(1)',
          popover: {
            title: 'Carga de documentos', description: 'En esta sección encontrará la lista de documentos a cargar, debe ocupar un espacio por cada documento.',
          },
        },
        {
          element: '.driver-alert-file-name',
          popover: {
            title: 'Nombres de documentos', description: 'Considere esta recomendación previo a comenzar la carga de documentación.',
          },
        },
        {
          element: '.driver-alert-original-file',
          popover: {
            title: 'Calidad de documentos', description: 'Estos requisitos son indicados por la autoridad educativa, por favor asegure que su documentos cuentan con ellos.',
          },
        },
        {
          element: '.driver-tabs .form-group:nth-child(1)',
          popover: {
            title: 'Selección', description: 'Dé clic dentro del recuadro donde va a cargar el documento; localice el documento y dé doble clic sobre el.',
          },
        },
        {
          element: '.driver-upload-files-button',
          popover: {
            title: 'Envío de documentos', description: 'Puede cargar uno o todos los documentos, lo importante es que dé clic en este botón para que se carguen en la plataforma, con ello se cambiará el estatus y Control Escolar podrá visualizarlos.',
          },
          onHighlighted: () => {
            currentTab.value = 0
          },
        },
        {
          element: '.driver-tabs li:nth-child(2)',
          popover: {
            title: 'Archivos previamente cargados', description: 'En esta sección encontrará los documentos que ya cargó y puede visualizar si se cargaron exitosamente dando clic sobre cada uno de ellos.',
          },
          onHighlighted: () => {
            currentTab.value = 1
          },
        },
        {
          element: '.driver-files-uploaded',
          popover: {
            title: 'Lista de documentos cargados', description: 'Al ingresar, también conocerá si ya fue validado el documento. Si el icono al principio de cada documento es color amarillo, significa que está pendiente de revisión; si es una color verde, este ya fue aprobado.',
          },
        },
        {
          element: '.driver-upload-files-later-button',
          popover: {
            title: 'Cargar después', description: 'Si no cuenta con sus documentos, puede cargarlos en otro momento. Al dar clic en este botón, se enviará la liga al correo que registró al darse de alta en esta plataforma. Esta liga es personal, no tiene vigencia, por lo que puede utilizarla las veces que lo necesite y solo para esta convocatoria.',
          },
        },
        {
          element: '.driver-interactive-guide-button',
          popover: {
            title: 'Guía interactiva', description: 'Si desea consultar nuevamente esta guía, dé clic en este botón.',
          },
        },
        {
          popover: {
            title: 'Muy bien, comencemos', description: 'Esperamos que este recorrido le sea de utilidad. Cualquier duda por favor contáctanos en Control Escolar, estamos para apoyarle.',
          },
          onDeselected: () => {
            window.document.cookie = 'isAlreadySeenInteractiveGuide=1; SameSite=None; Secure'
          },
        },
      ],
    })

    // * Show driver component
    const showDriverComponent = () => driverGuide.drive()

    // * Load document types
    const loadDocTypes = plan => {
      getDocumentTypesForPlan(plan)
        .then(response => {
          if (response.status !== 200) {
            toast({
              component: ToastificationContent,
              props: {
                title: 'Información de tipo de documentos',
                text: response.message,
                icon: 'AlertCircleIcon',
                variant: 'warning',
              },
            },
            { timeout: 8000 })
          }

          tipoDocs.value = response.data || []
          response.data.forEach(obj => {
            fileLoader.value[obj.subPath] = false
          })

          showContent.value = true

          if (response.status === 200 && response.data.length && !isAlreadySeenInteractiveGuide) {
            setTimeout(() => {
              showDriverComponent()
            })
          }
        }, error => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Atención',
              text: error,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          },
          { timeout: 10000 })
        })
    }

    //* Uploaded files
    const loadUploaded = () => {
      showLoaderUploaded.value = true
      uploadedDocuments({
        action: 31, x: id, t: time, plan: planId.value,
      })
        .then(response => {
          showLoaderUploaded.value = false
          if (response.status !== 200) {
            toast({
              component: ToastificationContent,
              props: {
                title: 'Información de documentos',
                text: response.message,
                icon: 'AlertCircleIcon',
                variant: 'warning',
              },
            },
            { timeout: 8000 })
          }

          loadedFiles.value = response.data || []
        }, error => {
          showLoaderUploaded.value = false
          showContent.value = true
          toast({
            component: ToastificationContent,
            props: {
              title: 'Atención',
              text: error,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          },
          { timeout: 10000 })
        })
    }

    //* Load person's id
    const loadData = () => {
      showLoader.value = true
      getPersonId(id, time)
        .then(response => {
          if (response.status === 200) {
            perId.value = response.x
            planId.value = response.plan
            showSendLater.value = true
            loadUploaded()
            loadDocTypes(response.plan)
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: 'Atención',
                text: response.message,
                icon: 'AlertCircleIcon',
                variant: 'warning',
              },
            },
            { timeout: 10000 })
            showError.value = true
            showContent.value = true
            messageError.value = response.message
          }

          showLoader.value = false
        }, error => {
          showError.value = true
          showContent.value = true
          messageError.value = error
          showLoader.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Atención',
              text: error,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          },
          { timeout: 10000 })
        })
    }

    //* On mounted fetch user's data
    onMounted(() => { loadData() })

    //* Send files later
    const sendLater = () => {
      const swalRedirect = () => {
        let timerInterval
        let secondsLeft = 5

        const Swal = vm.$swal
        Swal.fire({
          title: 'Carga de documentos pendiente',
          html: 'Será redireccionado automáticamente en <b>5</b> segundos.',
          timer: 5000,
          timerProgressBar: true,
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading()
            const timer = Swal.getPopup().querySelector('b')

            timerInterval = setInterval(() => {
              if (secondsLeft >= 0) secondsLeft -= 1

              timer.textContent = `${secondsLeft}`
            }, 1000)
          },
          willClose: () => {
            clearInterval(timerInterval)
          },
        }).then(result => {
          if (result.dismiss === 'timer') {
            router.push('/')
          }
        })
      }

      const sendMail = () => {
        showLoader.value = true
        sendLaterMail(id, time)
          .then(response => {
            showLoader.value = false
            showContent.value = false
            vm.$swal({
              title: 'Envío de correo',
              text: response.message,
              icon: 'success',
              customClass: {
                confirmButton: 'btn btn-primary',
              },
              buttonsStyling: false,
              confirmButtonText: 'Aceptar',
              allowOutsideClick: false,
            }).then(result => {
              if (result.value) swalRedirect()
            })
          }, error => {
            showLoader.value = false
            toast({
              component: ToastificationContent,
              props: {
                title: 'Atención',
                text: error,
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
            },
            { timeout: 10000 })
          })
      }

      const isLoggedIn = (store.state.cuenta.session.token && currentRol.value.id)

      let swalBody = {
        title: '¿Está seguro de cargar los documentos más tarde?',
        html: '<br />Si continúa, se enviará por correo la liga a este módulo para cargar su documentación más tarde, dicho correo será enviado a la cuenta de correo electrónico registrada en su proceso de inscripción.<br />',
      }

      if (isLoggedIn) {
        swalBody = {
          title: 'Opción deshabilitada temporalmente',
          html: '<br />Esta opción está pensada para el aspirante y <b>no personal administrativo</b>, por ello no se enviará correo alguno.<br />',
        }
      }

      vm.$swal({
        title: swalBody.title,
        html: swalBody.html,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Continuar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          if (isLoggedIn) {
            swalRedirect()
            return
          }

          sendMail()
        }
      })
    }

    //* Upload files
    const uploadFiles = () => {
      if (isEmptyObject(files.value)) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Envío de documentos',
            text: 'Asegúrese de cargar como mínimo un archivo',
            icon: 'FileIcon',
            variant: 'warning',
          },
        },
        { timeout: 10000 })
        return
      }

      if (!planId.value || planId.value === null || planId.value === '' || !perId.value || perId.value === null || perId.value === '') {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Envío de documentos',
            text: 'Ocurrió un error al generar el directorio, por favor recargue la página',
            icon: 'XIcon',
            variant: 'danger',
          },
        },
        { timeout: 10000 })
        return
      }

      Object.entries(files.value).forEach(file => {
        const [name, archivo] = file
        const idTipo = tipoDocs.value.find(tipo => tipo.subPath === name).id
        const alreadyUploaded = loadedFiles.value.find(doc => doc.tipo === idTipo)

        if (archivo && !isObject(alreadyUploaded) && alreadyUploaded === undefined) {
          if (!showUploadedAnyFile.value) showUploadedAnyFile.value = true
          fileLoader.value[name] = true

          store.dispatch('cuenta/handleFile', {
            file: archivo,
            ref: `alumnos/${perId.value}/documentos/plan-${planId.value}/${name}`,
            cat: 9,
            repo: 0,
            struct: 3,
          })
            .then(response => {
              const [dlvryResponse] = response.data.ids

              if (response.data.msg === 'OK') {
                assignAsPersonalDocument({
                  task: 'wspublico',
                  action: 32,
                  x: id,
                  t: time,
                  f: dlvryResponse.id,
                  td: tipoDocs.value.find(tipo => tipo.subPath === name).id || null,
                  plan: planId.value,
                })
                  .then(result => {
                    fileLoader.value[name] = false
                    const { icon, label } = formhttp(result.status)

                    if (result.status === 200) loadUploaded()
                    toast({
                      component: ToastificationContent,
                      props: {
                        title: 'Resultado de asignación',
                        text: result.message,
                        icon,
                        variant: label,
                      },
                    },
                    { timeout: 10000 })
                  }, error => {
                    fileLoader.value[name] = false
                    toast({
                      component: ToastificationContent,
                      props: {
                        title: 'Error al asignar documento',
                        text: error,
                        icon: 'AlertCircleIcon',
                        variant: 'danger',
                      },
                    },
                    { timeout: 10000 })
                  })
              } else {
                fileLoader.value[name] = false
                toast({
                  component: ToastificationContent,
                  props: {
                    title: 'Intento de guardado',
                    text: response.data.msg,
                    icon: response.icon,
                    variant: response.label,
                  },
                },
                { timeout: 10000 })
              }
            }, error => {
              fileLoader.value[name] = false
              toast({
                component: ToastificationContent,
                props: {
                  title: 'Atención',
                  text: error,
                  icon: 'AlertCircleIcon',
                  variant: 'danger',
                },
              },
              { timeout: 10000 })
            })
        }
      })
    }

    return {
      currentTab,
      files,
      loadedFiles,
      isEmptyObject,
      fileToPreview,
      tipoDocs,

      // Flags
      fileLoader,
      showLoader,
      showLoaderUploaded,
      showSendLater,
      showContent,
      showError,
      messageError,
      showUploadedAnyFile,
      uploadLaterModal,

      // Methods
      sendLater,
      uploadFiles,
      showDriverComponent,
      // previewFile,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>

<style scoped>
.card-container-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2;
}
</style>
